// On-the-wire envelope for an E2E message (ТИ-1, ТИ-2). The ciphertext lives in
// the message `content`; nothing here is secret, but nothing here is plaintext
// either. Versioned (`v`) for forward migration (ТИ-2).
//
// mime type carried in the message head so non-E2E clients can show a placeholder
// instead of garbage (ТИ-3 / ФТ-5).
export const OZ_MIME = 'application/x-oz-e2e';
export const OZ_WIRE_VERSION = 1;

export const b64enc = (u8) => {
  if (typeof Buffer !== 'undefined') return Buffer.from(u8).toString('base64');
  let s = ''; for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
  return btoa(s);
};
export const b64dec = (s) => {
  if (typeof Buffer !== 'undefined') return new Uint8Array(Buffer.from(s, 'base64'));
  const bin = atob(s); const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return u8;
};

// { init?, header:{dh,pn,n}, ct, tag } -> compact JSON string.
export function encodeEnvelope({ init, header, ct, tag }) {
  const env = {
    v: OZ_WIRE_VERSION,
    h: { dh: b64enc(header.dh), pn: header.pn, n: header.n },
    ct: b64enc(ct),
    tag: b64enc(tag),
  };
  if (init) {
    env.i = {
      idDH: b64enc(init.idDH),
      ek: b64enc(init.ek),
      opkId: init.opkId, // number | null
    };
  }
  return JSON.stringify(env);
}

export function decodeEnvelope(str) {
  const env = typeof str === 'string' ? JSON.parse(str) : str;
  const out = {
    version: env.v,
    header: { dh: b64dec(env.h.dh), pn: env.h.pn, n: env.h.n },
    ct: b64dec(env.ct),
    tag: b64dec(env.tag),
    init: null,
  };
  if (env.i) {
    out.init = { idDH: b64dec(env.i.idDH), ek: b64dec(env.i.ek), opkId: env.i.opkId };
  }
  return out;
}

// Group envelope (ФТ-9): the message body is encrypted ONCE with a random
// per-message content key K (AES-256-GCM: iv/ct/tag). K itself is then wrapped
// separately for every group member through that member's pairwise Double-Ratchet
// channel — `keys` maps memberUid -> a normal p2p envelope string carrying K as
// its plaintext. A member decrypts their own `keys[uid]` to recover K, then the
// body. The top-level `g` marks the envelope as a group one (vs the p2p shape).
export const OZ_GROUP_VERSION = 1;

export function encodeGroupEnvelope({ iv, ct, tag, keys }) {
  return JSON.stringify({
    v: OZ_WIRE_VERSION,
    g: OZ_GROUP_VERSION,
    iv: b64enc(iv),
    ct: b64enc(ct),
    tag: b64enc(tag),
    keys, // { uid: <p2p envelope string> } — already serialized, kept as-is
  });
}

// Returns { iv, ct, tag, keys } or null if `str` is not a group envelope.
export function decodeGroupEnvelope(str) {
  let env;
  try { env = typeof str === 'string' ? JSON.parse(str) : str; } catch { return null; }
  if (!env || !env.g || !env.keys) {
    return null;
  }
  return { iv: b64dec(env.iv), ct: b64dec(env.ct), tag: b64dec(env.tag), keys: env.keys };
}

// Serialize / restore a public prekey bundle for publishing in me.public.
export function encodeBundle(b) {
  return {
    idDH: b64enc(b.idDH), idSign: b64enc(b.idSign),
    spk: b64enc(b.spk), spkSig: b64enc(b.spkSig),
    opk: b.opk ? { id: b.opk.id, pub: b64enc(b.opk.pub) } : null,
  };
}
export function decodeBundle(b) {
  return {
    idDH: b64dec(b.idDH), idSign: b64dec(b.idSign),
    spk: b64dec(b.spk), spkSig: b64dec(b.spkSig),
    opk: b.opk ? { id: b.opk.id, pub: b64dec(b.opk.pub) } : null,
  };
}
